import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Search,
  CheckSquare,
  FlaskConical,
  Users,
  Loader2
} from 'lucide-react';
import { api } from '../../utils/api';

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

interface SearchResults {
  tasks: any[];
  labs: any[];
  joiners: any[];
}

export default function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>({
    tasks: [],
    labs: [],
    joiners: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults({ tasks: [], labs: [], joiners: [] });
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const [tasksRes, labsRes, joinersRes] = await Promise.allSettled([
          api.get(`/tasks/assignments/all`),
          api.get(`/lab/labs`),
          api.get(`/team/joiners/active`),
        ]);

        const lowerQuery = query.toLowerCase();

        const tasks = tasksRes.status === 'fulfilled' && tasksRes.value?.success
          ? (tasksRes.value.data || []).filter((t: any) =>
              (t.title || '').toLowerCase().includes(lowerQuery) ||
              (t.description || '').toLowerCase().includes(lowerQuery) ||
              (t.taskType || t.category || '').toLowerCase().includes(lowerQuery)
            ).slice(0, 5)
          : [];

        const labs = labsRes.status === 'fulfilled' && labsRes.value?.success
          ? (labsRes.value.data || []).filter((l: any) =>
              (l.title || l.name || '').toLowerCase().includes(lowerQuery) ||
              (l.description || '').toLowerCase().includes(lowerQuery) ||
              (l.type || '').toLowerCase().includes(lowerQuery)
            ).slice(0, 5)
          : [];

        const joiners = joinersRes.status === 'fulfilled' && joinersRes.value?.success
          ? (joinersRes.value.data || []).filter((j: any) =>
              (j.name || '').toLowerCase().includes(lowerQuery) ||
              (j.department || '').toLowerCase().includes(lowerQuery) ||
              (j.position || '').toLowerCase().includes(lowerQuery)
            ).slice(0, 5)
          : [];

        setResults({ tasks, labs, joiners });
      } catch (error) {
        console.error('Search error:', error);
        setResults({ tasks: [], labs: [], joiners: [] });
      } finally {
        setLoading(false);
      }
    }, 300); // Debounce

    return () => clearTimeout(timer);
  }, [query]);

  const totalResults = results.tasks.length + results.labs.length + results.joiners.length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Search Everything</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search tasks, labs, people..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>

        {query && !loading && (
          <div className="text-sm text-slate-600 px-1">
            {totalResults} {totalResults === 1 ? 'result' : 'results'} found
          </div>
        )}

        <ScrollArea className="h-[400px]">
          <div className="space-y-4">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-2 text-slate-600">Searching...</span>
              </div>
            )}

            {/* Tasks */}
            {!loading && results.tasks.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <CheckSquare className="w-4 h-4 text-slate-600" />
                  <h4 className="text-slate-800">Tasks</h4>
                  <Badge variant="secondary" className="text-xs">{results.tasks.length}</Badge>
                </div>
                <div className="space-y-2">
                  {results.tasks.map((task: any) => (
                    <div
                      key={task.id || task._id}
                      className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-slate-800">{task.title}</p>
                          <p className="text-xs text-slate-600 mt-1">{task.description}</p>
                        </div>
                        <Badge variant="outline" className="text-xs capitalize">
                          {task.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Labs */}
            {!loading && results.labs.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <FlaskConical className="w-4 h-4 text-slate-600" />
                  <h4 className="text-slate-800">Labs</h4>
                  <Badge variant="secondary" className="text-xs">{results.labs.length}</Badge>
                </div>
                <div className="space-y-2">
                  {results.labs.map((lab: any) => (
                    <div
                      key={lab.id || lab._id}
                      className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-slate-800">{lab.title || lab.name}</p>
                          <p className="text-xs text-slate-600 mt-1">{lab.description}</p>
                        </div>
                        <Badge variant="outline" className="text-xs capitalize">
                          {lab.difficulty || lab.type}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Joiners */}
            {!loading && results.joiners.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <Users className="w-4 h-4 text-slate-600" />
                  <h4 className="text-slate-800">Joiners</h4>
                  <Badge variant="secondary" className="text-xs">{results.joiners.length}</Badge>
                </div>
                <div className="space-y-2">
                  {results.joiners.map((joiner: any) => (
                    <div
                      key={joiner.id || joiner._id || joiner.email}
                      className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-slate-800">{joiner.name}</p>
                          <p className="text-xs text-slate-600 mt-1">
                            {joiner.position} • {joiner.department}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs capitalize">
                          {joiner.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!loading && query && totalResults === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Search className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-800 mb-1">No results found</p>
                <p className="text-sm text-slate-600">
                  Try adjusting your search terms
                </p>
              </div>
            )}

            {!query && !loading && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Search className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-slate-800 mb-1">Start searching</p>
                <p className="text-sm text-slate-600">
                  Search across tasks, labs, and more
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
